import { Box, BoxProps, Icon, Stack, styled, Typography } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { Article, ArticleErpInfo, PureListProps } from "@tm/models"
import { memo, useCallback, useRef, useState } from "react"
import { useLocation } from "react-router"
import { PartsListImageCoordinate } from "../../data/model"
import { ArticleGroupHeader } from "../list/components/ArticleGroupHeader/ArticleGroupHeader"
import SensitiveGraphics from "../list/components/sensitive-graphic"
import { ArticleListConfigurationProvider } from "../ListV2/ArticleListConfiguration"
import { ComposerComponent, ContextComposer } from "../ListV2/ContextProvider"
import { ActiveVehicleDataProvidersProvider } from "../ListV2/ContextProvider/ActiveVehicleDataProviders"
import { ArticleListActionsProvider } from "../ListV2/ContextProvider/ArticleListActions"
import { BasketQuantitiesProvider, WholesalerBasketQuantitiesProvider } from "../ListV2/ContextProvider/BasketQuantities"
import { EmptyFiltersProvider } from "../ListV2/ContextProvider/Filters"
import { ListOptionsProvider } from "../ListV2/ContextProvider/ListOptions"
import { NotesProvider } from "../ListV2/ContextProvider/Notes"
import { PreviouslyOrderedArticlesProvider } from "../ListV2/ContextProvider/PreviouslyOrderedArticles"
import { ProductGroupRepairTimesProvider } from "../ListV2/ContextProvider/ProductGroupRepairTimes"
import { SupplierLogosProvider } from "../ListV2/ContextProvider/SupplierLogos"
import { TradeReferenceProvider } from "../ListV2/ContextProvider/TradeReferences"
import { useArticleSelection } from "../ListV2/hooks/useArticleSelection"
import { ArticleGroupParams, WholesalerArticleGroup } from "../ListV2/models"
import { usePureListArticlesContext } from "./ContextProviders"
import { PureListArticlesProvider } from "./ContextProviders/Articles"
import { PureArticleItem } from "./PureArticleItem"
import { WholesalerArticleItem } from "../ListV2/components/WholesalerArticleItem/WholesalerArticleItem"
import { ArticlesWithOeArticles } from "./components/ArticlesAndOeArticles"
import { useArticleListFeaturesDisable } from "../ListV2/ArticleListConfiguration/ArticleListFeaturesDisable"

import { MultiErpInfosProvider } from "../ListV2/ContextProvider/ErpInfos"
import { useResizeErpColumnInDefaultArticleItem } from "../ListV2/hooks/useResizeErpContainerColumn"
import { PureArticleSelection } from "./PureArticleSelection"
import { EmptyListParamsProvider } from "../ListV2/ContextProvider/ListParams"
import { BasketMemoProvider } from "../ListV2/ContextProvider/BasketMemo"

const Container = styled(Box)({
    backgroundColor: "#fafafa",
    boxShadow: "0 1px 5px rgb(0 0 0 / 20%)",
    padding: "10px 5px 5px 20px",
    marginLeft: 16,
    zIndex: 1,
    marginTop: -10,
    position: "relative",
}) as typeof Box

const PureListComponent = memo(() => {
    const { translateText } = useLocalization()
    const { pathname } = useLocation()
    const { toggleSelectedArticle, selectedArticles } = useArticleSelection()
    const { articleGroups, articles, response, wholesalerArticles } = usePureListArticlesContext()
    const disableArticleGrouping = useArticleListFeaturesDisable((x) => x.disableArticleGrouping)
    const disableSelection = useArticleListFeaturesDisable((x) => x.disableSelection)

    const mainContainerRef = useRef<HTMLDivElement>(null)
    useResizeErpColumnInDefaultArticleItem(mainContainerRef.current, !!articleGroups.length)

    const listProps: BoxProps =
        pathname.includes("/modal") && !pathname.includes("/part-details")
            ? { height: "calc(100vh - 150px)", overflow: "auto", width: "calc(85vw - 50px)" }
            : {}

    const handleSelectImageCoordinate = useCallback(
        (coordinate: PartsListImageCoordinate) => {
            if (!articles) {
                return
            }
            const articlesWithCoordinate = articles.filter(
                (part) => part.internalId === coordinate.referencedArticle && part.imageCoordinates?.includes(`,${coordinate.id};`)
            )
            articlesWithCoordinate.forEach(toggleSelectedArticle)
        },
        [articles]
    )

    const renderArticle = (article: Article) => {
        if (article.linkedArticle) {
            return (
                <>
                    <Box sx={{ zIndex: 2 }}>
                        <PureArticleItem key={article.internalId} article={article} />
                    </Box>
                    <Container>
                        <PureArticleItem key={article.linkedArticle?.id} article={article.linkedArticle} />
                    </Container>
                </>
            )
        }

        return <PureArticleItem key={article.internalId} article={article} />
    }

    const renderArticles = () => {
        if (response?.type === "damageModulesList") {
            return <ArticlesWithOeArticles response={response} />
        }

        if (disableArticleGrouping) {
            return (
                <>
                    <Stack padding={0.5} key="WholesalerArticles">
                        {wholesalerArticles?.map((wholesalerArticle) => (
                            <WholesalerArticleItem key={wholesalerArticle.itemId} article={wholesalerArticle} />
                        ))}
                    </Stack>
                    <Stack padding={0.5}>{articles?.map(renderArticle)}</Stack>
                </>
            )
        }

        if (response?.type === "partslist" && response.partsListImage) {
            if (!articleGroups?.length) {
                return null
            }

            const flattened = articleGroups.flatMap((group) =>
                "productGroup" in group && Array.isArray(group.articles)
                    ? group.articles.map((article) => ({
                          article,
                          productGroup: group.productGroup,
                      }))
                    : []
            )

            const selected = flattened.filter(({ article }) => selectedArticles.some((s) => s.id === article.id))
            const unselected = flattened.filter(({ article }) => !selectedArticles.some((s) => s.id === article.id))

            const groupByProductGroup = (entries: typeof flattened) => {
                const map = new Map<number, { productGroup: { id: number; name: string }; articles: Article[] }>()
                entries.forEach(({ article, productGroup }) => {
                    if (!map.has(productGroup.id)) {
                        map.set(productGroup.id, { productGroup, articles: [] })
                    }
                    map.get(productGroup.id)!.articles.push(article)
                })
                return Array.from(map.values())
            }

            const groupedSelected = groupByProductGroup(selected)
            const groupedUnselected = groupByProductGroup(unselected)

            return (
                <>
                    {groupedSelected.length > 0 && (
                        <>
                            <Box bgcolor="#ffffff" sx={{ mt: 1 }}>
                                {translateText(752)}
                            </Box>
                            {groupedSelected.map((group) => (
                                <Stack key={`selected-${group.productGroup.id}`}>
                                    <ArticleGroupHeader
                                        title={group.productGroup.name}
                                        productGroupId={group.productGroup.id}
                                        groupArticlesCount={group.articles.length}
                                    />
                                    <Stack padding={0.5}>{group.articles.map(renderArticle)}</Stack>
                                </Stack>
                            ))}
                        </>
                    )}

                    {groupedUnselected.length > 0 && (
                        <>
                            <Box bgcolor="#ffffff" sx={{ mt: 1 }}>
                                {translateText(753)}
                            </Box>
                            {groupedUnselected.map((group) => (
                                <Stack key={`unselected-${group.productGroup.id}`}>
                                    <ArticleGroupHeader
                                        title={group.productGroup.name}
                                        productGroupId={group.productGroup.id}
                                        groupArticlesCount={group.articles.length}
                                    />
                                    <Stack padding={0.5}>{group.articles.map(renderArticle)}</Stack>
                                </Stack>
                            ))}
                        </>
                    )}
                </>
            )
        }

        return (
            <>
                {articleGroups.map((group) => {
                    if (group.type) {
                        if ((group as ArticleGroupParams).productGroup) {
                            const { productGroup, articles } = group as ArticleGroupParams
                            return (
                                <Stack key={productGroup.id}>
                                    <ArticleGroupHeader
                                        title={productGroup.name}
                                        productGroupId={productGroup.id}
                                        groupArticlesCount={articles.length}
                                    />
                                    <Stack padding={0.5}>{articles.map(renderArticle)}</Stack>
                                </Stack>
                            )
                        }
                    }
                    const { articles } = group as WholesalerArticleGroup
                    return (
                        <Stack padding={0.5} key="WholesalerArticles">
                            {articles.map((wholesalerArticle) => (
                                <WholesalerArticleItem key={wholesalerArticle.itemId} article={wholesalerArticle} />
                            ))}
                        </Stack>
                    )
                })}
            </>
        )
    }

    return (
        <Stack flex={1} position="relative">
            {response?.type === "alternative" && (
                <Stack direction="row" gap={0.5} padding={1}>
                    <Icon name="info" size="extrasmall" />
                    <Typography variant="label">{translateText(articles?.length ? 12893 : 13881)}</Typography>
                </Stack>
            )}
            {response?.type === "partslist" && response.partsListImage && (
                <Box p={0.5}>
                    <SensitiveGraphics
                        image={response.partsListImage}
                        imageCoordinates={response.imageCoordinates}
                        selectedArticles={selectedArticles}
                        onArticleSelect={handleSelectImageCoordinate}
                    />
                </Box>
            )}
            <>
                <PureArticleSelection selectionRef={mainContainerRef} />
                <Box {...listProps} ref={mainContainerRef}>
                    {renderArticles()}
                </Box>
            </>
        </Stack>
    )
})

export function PureList({ listId, listType }: PureListProps) {
    const [isArticlesLoaded, setIsArticlesLoaded] = useState(false)
    const [erpInfos, setErpInfos] = useState<ArticleErpInfo[]>([]) // provider can hold a global state, where useErpInfos would create a context dependent state

    const components: ComposerComponent[] = [
        [ArticleListConfigurationProvider],
        [EmptyListParamsProvider, { listType }],
        [BasketMemoProvider],
        [EmptyFiltersProvider],
        [PureListArticlesProvider, { listId, setIsLoaded: setIsArticlesLoaded, erpInfos }],
        [TradeReferenceProvider, { isEnabled: isArticlesLoaded }],
        [MultiErpInfosProvider, { isEnabled: isArticlesLoaded, erpInfos, setErpInfos }],
        [BasketQuantitiesProvider, { isEnabled: isArticlesLoaded }],
        [WholesalerBasketQuantitiesProvider, { isEnabled: isArticlesLoaded }],
        [ActiveVehicleDataProvidersProvider],
        [ProductGroupRepairTimesProvider, { isEnabled: isArticlesLoaded }],
        [NotesProvider, { isEnabled: isArticlesLoaded }],
        [PreviouslyOrderedArticlesProvider, { isEnabled: isArticlesLoaded }],
        [ListOptionsProvider, { isEnabled: true }],
        [SupplierLogosProvider, { isEnabled: isArticlesLoaded }],
        [ArticleListActionsProvider],
    ]
    return (
        <ContextComposer components={components}>
            <PureListComponent />
        </ContextComposer>
    )
}
